/**
 * ThreadNotifyBellButton — the per-thread notification bell as ONE
 * HeaderIconButton (header rail tiers, 2026-09-15).
 *
 * Extracted from ThreadHeaderActionButtons so two rails can mount the same
 * control: the WIDE rail (where it has lived since 2026-08-26) and the new
 * COMPACT rail, where it stays inline because it carries LIVE STATE — the
 * unseen-activity dot and the three-state glyph — while the housekeeping
 * buttons fold into the ⋯ menu. Presentational: the host owns the mode and
 * the cycle. `data-header-notify-btn` is the stable hook.
 */
import React from 'react';
import { HeaderIconButton, ThreadUnreadBadge } from './styles';
import type { ThreadNotifyMode } from '../../services/threadNotifications';
import { NOTIFY_MODE_PRESENTATION, notifyModeTitle } from './threadNotifyPresentation';

export interface ThreadNotifyBellButtonProps {
  threadId: string;
  threadTitle: string;
  notifyMode: ThreadNotifyMode;
  onCycleNotifyMode: (threadId: string) => void;
  osNotificationsBlocked?: boolean;
  otherThreadsUnseen?: boolean;
}

export const ThreadNotifyBellButton: React.FC<ThreadNotifyBellButtonProps> = ({
  threadId,
  threadTitle,
  notifyMode,
  onCycleNotifyMode,
  osNotificationsBlocked,
  otherThreadsUnseen,
}) => {
  const notifyTitle = notifyModeTitle(notifyMode, osNotificationsBlocked);
  return (
    <HeaderIconButton
      data-header-notify-btn=""
      data-notify-mode={notifyMode}
      data-os-notifications-blocked={osNotificationsBlocked ? 'true' : undefined}
      data-other-threads-unseen={otherThreadsUnseen ? 'true' : undefined}
      aria-label={`${notifyTitle} — thread ${threadTitle}${otherThreadsUnseen ? ' — other threads have new activity' : ''}`}
      title={notifyTitle}
      onClick={() => onCycleNotifyMode(threadId)}
    >
      {NOTIFY_MODE_PRESENTATION[notifyMode].icon}
      {otherThreadsUnseen && (
        <ThreadUnreadBadge aria-hidden="true" data-unread="dot" $dot $placement="corner" />
      )}
    </HeaderIconButton>
  );
};

export default ThreadNotifyBellButton;
